import { useMemo } from 'react';

const SHAPES = ['square', 'triangle', 'circle', 'zigzag', 'squiggle'];
const OPACITIES = [0.02, 0.04, 0.06, 0.08];

const getRandomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const getRandomFloat = (min, max) => Math.random() * (max - min) + min;

const ShapeElement = ({ type, size }) => {
  const color = 'var(--shape-color)';
  
  if (type === 'square') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100">
        <rect x="15" y="15" width="70" height="70" fill={color} />
      </svg>
    );
  }
  if (type === 'triangle') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100">
        <polygon points="50,15 90,85 10,85" fill={color} strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === 'circle') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="35" fill={color} />
      </svg>
    );
  }
  if (type === 'zigzag') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100">
        <polyline points="10,25 35,65 60,25 85,65" fill="none" stroke={color} strokeWidth="12" strokeLinejoin="miter" />
      </svg>
    );
  }
  if (type === 'squiggle') {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100">
        <path d="M 10 50 Q 25 20 50 50 T 90 50" fill="none" stroke={color} strokeWidth="12" strokeLinecap="round" />
      </svg>
    );
  }
  return null;
};

export default function ArcadeBackground() {
  const elements = useMemo(() => {
    const items = [];
    const COLS = 8;
    const ROWS = 5;
    for (let i = 0; i < 40; i++) {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      
      const cellWidth = 110 / COLS;
      const cellHeight = 110 / ROWS;
      
      // Base cell centers
      const cellCenterX = -5 + (col + 0.5) * cellWidth;
      const cellCenterY = -5 + (row + 0.5) * cellHeight;

      // Allow large random jitter so shapes can easily drift into neighboring cells and overlap
      const left = cellCenterX + getRandomFloat(-cellWidth * 1.2, cellWidth * 1.2);
      const top = cellCenterY + getRandomFloat(-cellHeight * 1.2, cellHeight * 1.2);

      items.push({
        id: i,
        type: SHAPES[getRandomInt(0, SHAPES.length - 1)],
        opacity: OPACITIES[getRandomInt(0, OPACITIES.length - 1)],
        left,
        top,
        size: getRandomInt(20, 100),
        rotation: getRandomInt(0, 360),
        rotationSpeed: getRandomFloat(20, 80),
        isReverse: Math.random() > 0.5,
        floatSpeed: getRandomFloat(15, 40),
        delay: getRandomFloat(0, -60),
        xDrift: getRandomFloat(-40, 40),
        yDrift: getRandomFloat(-40, 40)
      });
    }
    return items;
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: -1,
      pointerEvents: 'none',
      overflow: 'hidden'
    }}>
      <style>{`
        .arcade-grid {
          --grid-color: #0e0e0e;
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background-image: 
            linear-gradient(var(--grid-color) 1px, transparent 1px),
            linear-gradient(90deg, var(--grid-color) 1px, transparent 1px);
          background-size: 15px 15px;
          animation: grid-scroll 8s linear infinite;
        }
        :root.light-mode .arcade-grid {
          --grid-color: #f0f0f0;
        }
        .arcade-shape-wrapper {
          --shape-color: #ffffff;
          --opacity-multiplier: 1;
          position: absolute;
          animation: float var(--float-speed) ease-in-out infinite;
        }
        :root.light-mode .arcade-shape-wrapper {
          --shape-color: #000000;
          --opacity-multiplier: 2.5;
        }
        @keyframes grid-scroll {
          0% { background-position: 0px 0px; }
          100% { background-position: 15px 15px; }
        }
        .arcade-shape-rotator {
          width: 100%;
          height: 100%;
          animation-duration: var(--rot-speed);
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .arcade-shape-inner {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @keyframes float {
          0% { transform: translate(0, 0); }
          33% { transform: translate(var(--x-drift), var(--y-drift)); }
          66% { transform: translate(calc(var(--x-drift) * -0.5), calc(var(--y-drift) * -0.5)); }
          100% { transform: translate(0, 0); }
        }
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes rotate-reverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
      `}</style>

      <div className="arcade-grid"></div>
      
      {elements.map((el) => (
        <div 
          key={el.id} 
          className="arcade-shape-wrapper"
          style={{
            left: `${el.left}%`,
            top: `${el.top}%`,
            width: `${el.size}px`,
            height: `${el.size}px`,
            '--float-speed': `${el.floatSpeed}s`,
            '--x-drift': `${el.xDrift}px`,
            '--y-drift': `${el.yDrift}px`,
            animationDelay: `${el.delay}s`,
            opacity: `calc(${el.opacity} * var(--opacity-multiplier))`
          }}
        >
          <div 
            className="arcade-shape-rotator"
            style={{
              '--rot-speed': `${el.rotationSpeed}s`,
              animationName: el.isReverse ? 'rotate-reverse' : 'rotate',
              animationDelay: `${el.delay}s`
            }}
          >
            <div className="arcade-shape-inner" style={{ transform: `rotate(${el.rotation}deg)` }}>
              <ShapeElement type={el.type} size={el.size} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
